<main
  class="summary-page"
  [agrBlockSpinner]="state().status === 'loading'"
  [attr.aria-busy]="state().status === 'loading'"
>
  @if (state().status === 'error') {
    <section class="status-panel" data-test="summary-load-error">
      <agr-extended-notification
        [notificationType]="errorNotificationType"
        [title]="'dairyYieldScan.summary.loadError.title' | translate"
        [message]="'dairyYieldScan.summary.loadError.message' | translate"
        [actionText]="'dairyYieldScan.summary.loadError.retry' | translate"
        (action)="loadSummary()"
      ></agr-extended-notification>
    </section>
  } @else if (state().status === 'notFound') {
    <section class="status-panel" data-test="summary-not-found">
      <agr-extended-notification
        [notificationType]="errorNotificationType"
        [title]="'dairyYieldScan.summary.notFound.title' | translate"
        [message]="'dairyYieldScan.summary.notFound.message' | translate"
        [actionText]="'dairyYieldScan.summary.notFound.action' | translate"
        (action)="startNewScan()"
      ></agr-extended-notification>
    </section>
  } @else if (summary(); as summary) {
    <section class="result-hero">
      <div class="hero-content">
        <p class="eyebrow light">{{ 'dairyYieldScan.summary.hero.eyebrow' | translate }}</p>
        <h1>
          {{
            (summary.baselinePotential === 0
              ? 'dairyYieldScan.summary.hero.reachedPotential'
              : 'dairyYieldScan.summary.hero.title'
            ) | translate
          }}
        </h1>
        <article class="total-potential">
          <span>{{ 'dairyYieldScan.summary.hero.totalLabel' | translate }}</span>
          <strong>{{ summary.baselinePotential | number: '1.1-1' }} kg</strong>
          <small>{{ 'dairyYieldScan.summary.hero.totalUnit' | translate }}</small>
        </article>
      </div>
    </section>

    <div class="summary-content">
      @let customer = customerDisplay(summary);
      <section class="customer-section" aria-labelledby="customer-heading">
        <h2 id="customer-heading">{{ 'dairyYieldScan.customer.title' | translate }}</h2>
        <dl class="customer-details">
          <div>
            <dt>{{ 'dairyYieldScan.fields.isExistingAgrifirmCustomer' | translate }}</dt>
            <dd data-test="customer-status">{{ customer.statusKey | translate }}</dd>
          </div>
          @if (customer.number !== null) {
            <div>
              <dt>{{ 'dairyYieldScan.fields.customerNumber' | translate }}</dt>
              <dd data-test="customer-number">{{ customer.number }}</dd>
            </div>
          }
          <div>
            <dt>{{ 'dairyYieldScan.fields.buysAgrifirmCompoundFeed' | translate }}</dt>
            <dd data-test="customer-feed">{{ customer.feedKey | translate }}</dd>
          </div>
        </dl>
      </section>
      <section class="category-section" aria-labelledby="category-heading">
        <h2 id="category-heading">{{ 'dairyYieldScan.summary.categories.title' | translate }}</h2>
        <app-dairy-yield-scan-category-potentials
          [categoryPotentials]="summary.categoryPotentials"
        />
      </section>

      <app-dairy-yield-scan-insights
        [compliments]="summary.compliments"
        [improvements]="summary.improvements"
      />

      <section class="figures-section" aria-labelledby="figures-heading">
        <h2 id="figures-heading">{{ 'dairyYieldScan.summary.figures.title' | translate }}</h2>
        <div class="figures-grid">
          <article class="milk-figures">
            <div>
              <span>{{ 'dairyYieldScan.summary.figures.potentialMilk' | translate }}</span>
              @if (annualPotentialMilk() !== null) {
                <strong>{{ annualPotentialMilk() | number: '1.0-0' }} kg</strong>
                <small>
                  {{ 'dairyYieldScan.summary.figures.perYearPrefix' | translate }}
                  {{ summary.dairyCowCount }}
                  {{ 'dairyYieldScan.summary.figures.cows' | translate }}
                </small>
              } @else {
                <strong class="unavailable">{{
                  'dairyYieldScan.summary.figures.unavailable' | translate
                }}</strong>
              }
            </div>
            <div>
              <span>{{ 'dairyYieldScan.summary.figures.targetMilk' | translate }}</span>
              @if (annualTargetMilk() !== null) {
                <strong>{{ annualTargetMilk() | number: '1.0-0' }} kg</strong>
                <small>
                  {{ 'dairyYieldScan.summary.figures.perYearPrefix' | translate }}
                  {{ summary.dairyCowCount }}
                  {{ 'dairyYieldScan.summary.figures.cows' | translate }}
                </small>
              } @else {
                <strong class="unavailable">{{
                  'dairyYieldScan.summary.figures.unavailable' | translate
                }}</strong>
              }
            </div>
          </article>

          <article class="milk-price-card">
            <mat-form-field appearance="outline">
              <mat-label>{{ 'dairyYieldScan.summary.figures.milkPrice' | translate }}</mat-label>
              <span matTextPrefix>€&nbsp;</span>
              <label class="cdk-visually-hidden" for="milk-price">
                {{ 'dairyYieldScan.summary.figures.milkPrice' | translate }}
              </label>
              <input
                id="milk-price"
                matInput
                agrNumber
                inputmode="decimal"
                [step]="0.01"
                [formControl]="milkPriceControl"
              />
              <span matTextSuffix class="milk-price-unit">/ 100 kg</span>
              @if (milkPriceControl.invalid) {
                <mat-error>{{
                  'dairyYieldScan.summary.figures.invalidMilkPrice' | translate
                }}</mat-error>
              }
            </mat-form-field>
            <p>{{ 'dairyYieldScan.summary.figures.milkPriceHelp' | translate }}</p>
          </article>

          <article class="revenue-card">
            <div>
              <span>{{ 'dairyYieldScan.summary.figures.potentialRevenue' | translate }}</span>
              @if (annualPotentialRevenue() !== null) {
                <strong>€ {{ annualPotentialRevenue() | number: '1.0-0' }}</strong>
                <small>{{ 'dairyYieldScan.summary.figures.revenueUnit' | translate }}</small>
              } @else {
                <strong class="unavailable">{{
                  'dairyYieldScan.summary.figures.unavailable' | translate
                }}</strong>
              }
            </div>
            <div>
              <span>{{ 'dairyYieldScan.summary.figures.targetRevenue' | translate }}</span>
              @if (annualTargetRevenue() !== null) {
                <strong>€ {{ annualTargetRevenue() | number: '1.0-0' }}</strong>
                <small>{{ 'dairyYieldScan.summary.figures.revenueUnit' | translate }}</small>
              } @else {
                <strong class="unavailable">{{
                  'dairyYieldScan.summary.figures.unavailable' | translate
                }}</strong>
              }
            </div>
          </article>
        </div>

        <aside class="financial-warning">
          <mat-icon aria-hidden="true">warning_amber</mat-icon>
          <p>{{ 'dairyYieldScan.summary.warning.message' | translate }}</p>
        </aside>
      </section>

      <app-dairy-yield-scan-actions
        [scanId]="summary.id"
        [improvements]="summary.actionPoints"
        [actionPlan]="summary.actionPlan"
        [currentMilkYield]="currentMilkYield()"
        [targetMilkYield]="targetMilkYield()"
      />
      <div class="summary-actions">
        <button mat-stroked-button color="primary" type="button" (click)="printSummary()">
          <mat-icon>print</mat-icon>
          {{ 'dairyYieldScan.summary.print.action' | translate }}
        </button>
      </div>
    </div>
  }
</main>

@if (summary(); as summary) {
  <app-dairy-yield-scan-print-summary
    [summary]="summary"
    [milkPrice]="milkPriceControl.value"
    [currentMilkYield]="currentMilkYield()"
    [targetMilkYield]="targetMilkYield()"
  />
}
